import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, FormControlLabel, Radio, RadioGroup, Snackbar, Alert, Typography,
} from '@mui/material';
import { salvarReenvioWhatsapp } from '../../../../services/core/processos/processosService';

const POS_PROCESSAMENTO = '0';
const POR_FASE = '1';
const MENSAGEM_ERRO = 'Erro ao Salvar o Reenvio de Whatsapp.';

/**
 * Espelha `AcaoReenviarWhatsapp.jsx` (tela real de Inspecionar Processo, AW2-912)
 * com fidelidade total — mesma regra de habilitação/valor inicial, mesmo service.
 *
 * Portada 1:1 de `statusContatoDefinitionInicial`: quando as duas opções estão
 * disponíveis, o real prioriza "Pós Processamento do Borderô" ('0'), não
 * "Processos em uma fase específica".
 */
function valorInicial(existePorFase, existePosProducao) {
  if (existePorFase && !existePosProducao) {
    return POR_FASE;
  }
  return POS_PROCESSAMENTO;
}

function AcaoReenviarWhatsappProcesso({
  idProcesso, idFaseAtual, podeReenviarWhatsapp, onSucesso,
}) {
  const existePosProducao = Boolean(podeReenviarWhatsapp?.includes('POS PROCESSAMENTO'));
  const existePorFase = Boolean(podeReenviarWhatsapp?.includes('POR FASE'));

  const [statusContato, setStatusContato] = useState(
    () => valorInicial(existePorFase, existePosProducao),
  );
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState('');

  const onSubmit = useCallback(async () => {
    try {
      setEnviando(true);
      await salvarReenvioWhatsapp(idProcesso, idFaseAtual, statusContato);
      // Sem Snackbar de sucesso local: onSucesso() fecha este painel
      // imediatamente (mesmo padrão de mútua exclusão da Etapa 4), o que
      // desmontaria um Snackbar renderizado aqui antes de ficar visível.
      // O feedback de sucesso é responsabilidade de quem chama onSucesso.
      onSucesso();
    } catch {
      setErro(MENSAGEM_ERRO);
    } finally {
      setEnviando(false);
    }
  }, [idProcesso, idFaseAtual, statusContato, onSucesso]);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Reenviar Whatsapp
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        Escolha o Tipo do Contato:
      </Typography>
      <RadioGroup
        aria-label="Tipo do Contato"
        value={statusContato}
        onChange={e => setStatusContato(e.target.value)}
      >
        <FormControlLabel
          value={POS_PROCESSAMENTO}
          disabled={!existePosProducao}
          control={<Radio />}
          label="Pós Processamento do Borderô"
        />
        <FormControlLabel
          value={POR_FASE}
          disabled={!existePorFase}
          control={<Radio />}
          label="Processos em uma fase específica"
        />
      </RadioGroup>
      <Box sx={{ mt: 2 }}>
        <Button variant="contained" color="success" disabled={enviando} onClick={onSubmit}>
          Concluir
        </Button>
      </Box>

      <Snackbar
        open={Boolean(erro)}
        autoHideDuration={4000}
        onClose={() => setErro('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" variant="filled" onClose={() => setErro('')}>
          {erro}
        </Alert>
      </Snackbar>
    </Box>
  );
}

AcaoReenviarWhatsappProcesso.propTypes = {
  idProcesso: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  idFaseAtual: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  podeReenviarWhatsapp: PropTypes.arrayOf(PropTypes.string),
  onSucesso: PropTypes.func.isRequired,
};

AcaoReenviarWhatsappProcesso.defaultProps = {
  idProcesso: null,
  idFaseAtual: null,
  podeReenviarWhatsapp: [],
};

export default React.memo(AcaoReenviarWhatsappProcesso);
